import { useDeepCompareEffect } from "@react-hookz/web";
import { useCallback, useEffect, useState } from "react";

import type { Props } from "~/components/advertisement/ad-slots/ad-slot";
import * as aps from "~/components/advertisement/amazon-publisher-service/hooks";
import * as md from "~/components/advertisement/magnite-demand-manager/hooks";
import { config } from "~/data";

export const useAdSlotWithRefresh = (props: Exclude<Props, "manualRefreshCount">) => {
  const [manualRefreshCount, setManualRefreshCount] = useState(0);

  useDeepCompareEffect(() => {
    setManualRefreshCount(0);
  }, [props]);

  return {
    props: {
      ...props,
      manualRefreshCount,
    },
    refresh: useCallback(() => setManualRefreshCount(v => v + 1), [setManualRefreshCount]),
  };
};

export const useRenderSlotsWithBids = (slot: googletag.Slot | null, refreshCount?: number) => {
  const { handleFetchBids: handleFetchApsBids } = aps.useFetchBids(slot);
  const { handleFetchBids: handleFetchMdBids } = md.useFetchBids(slot);

  useEffect(() => {
    if (!slot) return;

    const getFailsafeTimeoutPromise = () =>
      new Promise<void>(resolve => {
        setTimeout(resolve, config.advertisement.failsafeTimeout);
      });

    let hasCancel = false;

    const run = async () => {
      await Promise.race([
        Promise.all([handleFetchApsBids(), handleFetchMdBids()]),
        getFailsafeTimeoutPromise(),
      ]);
      if (!hasCancel) {
        googletag.cmd.push(() => {
          googletag.pubads().refresh([slot]);
        });
      }
    };
    void run();

    return () => {
      hasCancel = true;
    };
  }, [slot, refreshCount, handleFetchApsBids, handleFetchMdBids]);
};
